import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createUseThemedStyles } from '@/hooks';

const OTP_LENGTH = 6;

const useStyles = createUseThemedStyles((theme) => ({
	otpInput: {
		display: 'flex',
		gap: '8px',
		alignItems: 'center',
	},
	digit: ({ hasError }: { hasError: boolean }) => ({
		width: 40,
		height: 40,
		padding: '7px 13px',
		textAlign: 'center',
		fontFamily: 'inherit',
		fontSize: 16,
		lineHeight: 1.5,
		color: theme.colors.gray900,
		backgroundColor: theme.colors.white,
		border: `1px solid ${hasError ? theme.colors.danger : theme.colors.gray400}`,
		borderRadius: 0,
		outline: 'none',
		'&:focus': {
			borderColor: hasError ? theme.colors.danger : theme.colors.primary,
			boxShadow: `0 0 0 1px ${hasError ? theme.colors.danger : theme.colors.primary}`,
		},
		'&:disabled': {
			backgroundColor: theme.colors.gray200,
		},
		MozAppearance: 'textfield',
		'&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
			WebkitAppearance: 'none',
			margin: 0,
		},
	}),
}));

interface OtpInputProps {
	value: string;
	onChange(value: string): void;
	onComplete?(value: string): void;
	disabled?: boolean;
	autoFocus?: boolean;
	name?: string;
	hasError?: boolean;
}

export const OtpInput = ({
	value,
	onChange,
	onComplete,
	disabled,
	autoFocus,
	name,
	hasError = false,
}: OtpInputProps) => {
	const classes = useStyles({ hasError });
	const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

	const digits = useMemo(() => {
		const padded = value.padEnd(OTP_LENGTH, ' ');
		return Array.from({ length: OTP_LENGTH }, (_, i) => {
			const ch = padded[i];
			return ch === ' ' ? '' : ch;
		});
	}, [value]);

	useEffect(() => {
		if (autoFocus) {
			inputsRef.current[0]?.focus();
		}
	}, [autoFocus]);

	const setDigitAt = useCallback(
		(index: number, digit: string) => {
			const chars = digits.slice();
			chars[index] = digit;
			const next = chars.join('').replace(/\s/g, '');
			onChange(next);
			return next;
		},
		[digits, onChange]
	);

	const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const sanitized = event.target.value.replace(/\D/g, '');
		if (!sanitized) {
			setDigitAt(index, '');
			return;
		}

		if (sanitized.length > 1) {
			const chars = digits.slice();
			let cursor = index;
			for (const ch of sanitized) {
				if (cursor >= OTP_LENGTH) break;
				chars[cursor] = ch;
				cursor += 1;
			}
			const next = chars.join('').replace(/\s/g, '');
			onChange(next);
			const focusIndex = Math.min(cursor, OTP_LENGTH - 1);
			inputsRef.current[focusIndex]?.focus();
			if (next.length === OTP_LENGTH) {
				onComplete?.(next);
			}
			return;
		}

		const next = setDigitAt(index, sanitized);
		if (index < OTP_LENGTH - 1) {
			inputsRef.current[index + 1]?.focus();
		}
		if (next.length === OTP_LENGTH) {
			onComplete?.(next);
		}
	};

	const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Backspace') {
			if (digits[index]) {
				setDigitAt(index, '');
				return;
			}
			if (index > 0) {
				event.preventDefault();
				setDigitAt(index - 1, '');
				inputsRef.current[index - 1]?.focus();
			}
			return;
		}

		if (event.key === 'ArrowLeft' && index > 0) {
			event.preventDefault();
			inputsRef.current[index - 1]?.focus();
			return;
		}

		if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
			event.preventDefault();
			inputsRef.current[index + 1]?.focus();
		}
	};

	const handlePaste = (index: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
		const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
		if (!pasted) return;
		event.preventDefault();

		const chars = digits.slice();
		let cursor = index;
		for (const ch of pasted) {
			if (cursor >= OTP_LENGTH) break;
			chars[cursor] = ch;
			cursor += 1;
		}
		const next = chars.join('').replace(/\s/g, '');
		onChange(next);
		const focusIndex = Math.min(cursor, OTP_LENGTH - 1);
		inputsRef.current[focusIndex]?.focus();
		if (next.length === OTP_LENGTH) {
			onComplete?.(next);
		}
	};

	return (
		<div className={classes.otpInput} role="group" aria-label={name || 'One-time password'}>
			{digits.map((digit, index) => (
				<input
					key={index}
					ref={(el) => (inputsRef.current[index] = el)}
					className={classes.digit}
					type="text"
					inputMode="numeric"
					pattern="[0-9]*"
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="off"
					spellCheck={false}
					maxLength={1}
					value={digit}
					disabled={disabled}
					aria-label={`Digit ${index + 1}`}
					onChange={handleChange(index)}
					onKeyDown={handleKeyDown(index)}
					onPaste={handlePaste(index)}
					onFocus={(event) => event.target.select()}
				/>
			))}
		</div>
	);
};
